import { useRouter } from "next/router";
import React, { ReactElement } from "react";
import { useIntl } from "react-intl";

import {
  BookingFormSectionProps,
  useBookingFormSection,
} from "shared/hooks/bookingFormSection";

import Img from "@components/FileServer/Img";
import CbtSaveGuestCheckbox from "@components/UI/Form/CbtSaveGuestCheckbox";
import CbtUpdateGuestCheckbox from "@components/UI/Form/CbtUpdateGuestCheckbox";
import PassengerCbtGuestSearch from "@components/UI/Form/PassengerCbtGuestSearch";
import FormattedMessage from "@components/UI/FormattedMessage";
import LoaderMini from "@components/UI/LoaderMini";

import BookingFormElement from "./BookingFormElement";
import BookingFormInfoModal from "./BookingFormInfoModal";
import BookingFormPassengers from "./BookingFormPassenger";

export default function BookingFormSection({
  index,
}: BookingFormSectionProps): ReactElement {
  const intl = useIntl();
  const router = useRouter();

  const {
    toggleIsCollapsed,
    uneditablePassengerName,
    bookingFormSection,
    bookingFormSection: { collapsible, isCollapsed },
    title,
    showCbtGuestSearchForm,
    indexGuest,
    showBookingFormSection,
    showUpdateCbtGuestForLater,
    showCbtGuestAddNewGuestButton,
    deletePredefinedGuest,
    setShowCbtGuestSearchForm,
    showSaveCbtGuestForLater,
    showLCCText,
    showModal,
    modalSectionHandler,
    modalData,
    hiddenFields,
    isFetchingServices,
    backgroundImageUrl,
    isHotelRoomForm,
  } = useBookingFormSection({
    index,
    intl,
    query: router.query,
  });

  if (!showBookingFormSection) {
    return null;
  }

  const hasFooterSection = bookingFormSection?.modals?.length > 0;
  const isMarketplace = bookingFormSection?.code?.includes("marketplace");
  const hasPassengerServices = bookingFormSection?.code?.includes(
    "passengerServices"
  );

  const hasServicesInOthers =
    bookingFormSection?.code.includes("other") &&
    bookingFormSection?.elements?.includes("LuggageOptions");

  const waitForMarketplaceRecalculation =
    isFetchingServices &&
    (isMarketplace || hasPassengerServices || hasServicesInOthers);

  return (
    <>
      <div className={backgroundImageUrl ? "result-form-box-wrapper" : ""}>
        {title && (
          <>
            {isHotelRoomForm && bookingFormSection?.isFirstInRoomSelection && (
              <h2
                role="presentation"
                className={`h3 result-h3-room-title ${
                  collapsible ? "pointer noselect" : ""
                }`}
              >
                {bookingFormSection?.roomName}
              </h2>
            )}
            {isHotelRoomForm && bookingFormSection.code === "contact" && (
              <>
                <div className="result-h3-extra-space-hotels" />
                <div className="result-h3-extra-border-hotels" />
              </>
            )}
            <h3
              role="presentation"
              className={`h3 result-h3
              } ${collapsible ? "pointer noselect" : ""}`}
              id={collapsible ? "form-box-collapsible-heading" : ""}
              style={collapsible ? { justifyContent: "normal" } : {}}
              onClick={toggleIsCollapsed}
            >
              {title}
              {collapsible && (
                <Img
                  src="/static/images/ico-arrow-down.svg"
                  style={{
                    marginLeft: "15px",
                    transition: "all .4s ease",
                    transform: !isCollapsed ? "rotate(180deg)" : null,
                  }}
                />
              )}
              <div className="result-h3-extra-wrapper">
                {showCbtGuestAddNewGuestButton && (
                  <span
                    role="button"
                    className="result-h3-extra-link"
                    id={`choose-different-guest-${indexGuest}`}
                    onClick={() => {
                      deletePredefinedGuest();
                      setShowCbtGuestSearchForm(true);
                    }}
                  >
                    <FormattedMessage id="PassengerCBTGuestSearch.selectDifferent" />
                  </span>
                )}

                {showSaveCbtGuestForLater && (
                  <CbtSaveGuestCheckbox indexPassenger={Number(indexGuest)} />
                )}

                {showUpdateCbtGuestForLater && (
                  <CbtUpdateGuestCheckbox indexPassenger={Number(indexGuest)} />
                )}
              </div>
            </h3>
          </>
        )}
        {bookingFormSection.code === "passengerPerson" && (
          <BookingFormPassengers
            bookingFormPassengerSection={bookingFormSection}
          />
        )}
        {!isCollapsed && (
          <div
            className={`result-form-box ${
              hasFooterSection ? "has-footer-section" : 0
            }`}
          >
            {waitForMarketplaceRecalculation && (
              <div className="form-section-loader">
                <div className="form-section-loader-wrapper">
                  <LoaderMini />
                </div>
              </div>
            )}
            {showLCCText && (
              <div className="form-section-description">
                <span>
                  <FormattedMessage id="ResultForm.LccCredentialsHint" />
                </span>
              </div>
            )}
            {uneditablePassengerName && (
              <div className="form-text-input-container form-text-input-container-uneditable-name">
                <span className="form-text-input-label">
                  <FormattedMessage id="PassengerUneditableName.name" />
                </span>

                <span
                  className="form-text-input-uneditable-name"
                  id={`${indexGuest}-form-text-input-uneditable-name`}
                >
                  {uneditablePassengerName}
                </span>
              </div>
            )}
            {showCbtGuestSearchForm && (
              <div>
                <PassengerCbtGuestSearch
                  setShowCbtGuestSearchForm={setShowCbtGuestSearchForm}
                  indexGuest={Number(indexGuest)}
                />
              </div>
            )}
            {!showCbtGuestSearchForm && (
              <>
                {bookingFormSection?.description && (
                  <div className="form-section-description">
                    <span>{bookingFormSection.description}</span>
                  </div>
                )}
                {bookingFormSection.elements.map((bookingFormElement) => {
                  if (hiddenFields.includes(bookingFormElement)) {
                    return;
                  }

                  return (
                    <BookingFormElement
                      bookingFormElement={bookingFormElement}
                      alert={bookingFormSection.alert}
                      key={bookingFormElement}
                    />
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
      {hasFooterSection && (
        <div className="flight-additional-info flight-additional-info-desktop-invalid">
          {isFetchingServices && isMarketplace && (
            <div className="flight-additional-info-loader" />
          )}
          <div />
          <div className="flight-detail-desktop-links">
            {bookingFormSection?.modals?.map((modal, i) => {
              const showSeparator =
                bookingFormSection?.modals?.length > 1 &&
                i + 1 !== bookingFormSection?.modals?.length;

              return (
                <div
                  className="additional-detail-section-links-wrapper"
                  key={i}
                >
                  <span
                    role="button"
                    onClick={() =>
                      modalSectionHandler(
                        modal?.DataSource?.GolApiRequest?.RequestData
                      )
                    }
                    className="link pointer"
                  >
                    {modal?.Label?.$t}
                  </span>
                  <BookingFormInfoModal
                    show={showModal}
                    onClose={modalSectionHandler}
                    title={modalData.title}
                    content={modalData.content}
                    showErrorText={modalData.showErrorText}
                  />
                  {showSeparator && (
                    <span className="text-separator">{" | "}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
